import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import AdminLayout from "../../components/AdminLayout";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchReports();
  }, [adminToken, statusFilter]);

  const fetchReports = async () => {
    try {
      const url = statusFilter && statusFilter !== "all" ? `${API}/admin/reports?status=${statusFilter}` : `${API}/admin/reports`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setReports(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching reports");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (reportId, action, reason = null) => {
    try {
      await axios.post(
        `${API}/admin/reports/${reportId}/action`,
        { action, reason },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success(`Report ${action === 'approve' ? 'dismissed' : action + 'ed'}`);
      fetchReports();
    } catch (error) {
      toast.error("Error taking action");
    }
  };

  const getReportTypeBadge = (type) => {
    const styles = {
      safety_concern: "bg-red-500/10 text-red-400 border-red-500/20",
      user_complaint: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      technical_issue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      feedback: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
    };
    return styles[type] || styles.feedback;
  };

  return (
    <AdminLayout title="Reports" subtitle="Review and take action on user reports">
      {/* Filter */}
      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-slate-800 border-slate-600 text-white">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All Reports</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-rose-500">Loading reports...</div>
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-slate-800 rounded-xl sm:rounded-2xl p-8 sm:p-12 border border-slate-700 text-center">
          <span className="text-5xl block mb-4">✅</span>
          <h3 className="text-xl text-white mb-2">No Reports Found</h3>
          <p className="text-slate-400 text-sm">
            {statusFilter === "pending" ? "All caught up! No pending reports." : "No reports matching this filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div 
              key={report.id}
              className="bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-slate-700"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    report.report_type === 'safety_concern' ? 'bg-red-500/10' :
                    report.report_type === 'user_complaint' ? 'bg-amber-500/10' :
                    'bg-blue-500/10'
                  }`}>
                    <span className="text-xl">
                      {report.report_type === 'safety_concern' ? '🚨' :
                       report.report_type === 'user_complaint' ? '⚠️' : '📝'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-white font-medium capitalize text-sm sm:text-base">
                        {report.report_type.replace('_', ' ')}
                      </h3>
                      <span className={`px-2 py-0.5 rounded text-xs border ${getReportTypeBadge(report.report_type)}`}>
                        {report.category || 'General'}
                      </span>
                      {report.status === 'pending' ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20">
                          Pending
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          Resolved
                        </span>
                      )}
                    </div>
                    
                    {report.reported_user_name && (
                      <p className="text-slate-400 text-sm mb-2">
                        Reported User: <span className="text-white">{report.reported_user_name}</span>
                      </p>
                    )}
                    
                    <p className="text-slate-300 mb-3 text-sm sm:text-base">{report.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-slate-500 text-xs sm:text-sm">
                      <span className="flex items-center gap-1">
                        🕐 {new Date(report.created_at).toLocaleString()}
                      </span>
                      {report.contact_email && (
                        <span className="truncate">📧 {report.contact_email}</span>
                      )}
                    </div>
                  </div>
                </div>

                {report.status === 'pending' && (
                  <div className="flex flex-wrap gap-2 lg:flex-nowrap">
                    <Button
                      size="sm"
                      onClick={() => handleAction(report.id, 'warn', 'Warning issued based on report')}
                      className="bg-amber-600 text-white hover:bg-amber-700 text-xs sm:text-sm"
                    >
                      Warn
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAction(report.id, 'ban', 'Banned based on report')}
                      className="bg-red-600 text-white hover:bg-red-700 text-xs sm:text-sm"
                    >
                      🚫 Ban
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAction(report.id, 'dismiss')}
                      variant="outline"
                      className="border-slate-600 text-slate-300 hover:bg-slate-700 text-xs sm:text-sm"
                    >
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>

              {report.resolution_action && (
                <div className="mt-4 pt-4 border-t border-slate-700">
                  <p className="text-slate-400 text-xs sm:text-sm">
                    Resolved: <span className="text-emerald-400 capitalize">{report.resolution_action}</span>
                    {report.resolution_reason && ` - ${report.resolution_reason}`}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminReportsPage;
